import type { ReactNode } from "react";
import { Check } from "lucide-react";
import type { InviteConsents } from "@/lib/types";

/**
 * The unbundled beta consent checkboxes (private-beta Phase 2), shared by
 * every path that grants a team membership: `/invite/{token}` acceptance
 * and the access-request form on `/register`. One implementation so the
 * two paths can't drift into asking for different things — the server
 * validates both against the same manifest in `services/consents.py`.
 *
 * `playerCount > 0` means the joiner is consenting ON BEHALF OF children
 * (a guardian linking to players), which is what turns on the per-child
 * items. A player linking to their own roster entry passes 0.
 */
export const EMPTY_CONSENTS: InviteConsents = {
  agreement: false,
  privacy_notice: false,
  player_roster: false,
  player_video: false,
  player_feedback: false,
  player_photo: false,
};

export function requiredConsentsMet(
  consents: InviteConsents,
  playerCount: number,
): boolean {
  if (!consents.agreement || !consents.privacy_notice) return false;
  if (playerCount > 0) {
    return Boolean(
      consents.player_roster && consents.player_video && consents.player_feedback,
    );
  }
  return true;
}

function ConsentCheckbox({
  checked,
  onChange,
  testId,
  children,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  testId: string;
  children: ReactNode;
}) {
  return (
    <label className="flex items-start gap-2 text-xs text-ink">
      <span className="tktic-consent-checkbox-shell">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          data-testid={testId}
          className="tktic-consent-checkbox"
        />
        <Check
          aria-hidden="true"
          className="tktic-consent-checkbox-check"
          strokeWidth={2.5}
        />
      </span>
      <span className="leading-5">{children}</span>
    </label>
  );
}

export function ConsentPanel({
  playerCount,
  consents,
  onChange,
}: {
  /** How many children this person is consenting FOR. 0 hides the
   * per-child items (adult-only affirmation). */
  playerCount: number;
  consents: InviteConsents;
  onChange: (next: InviteConsents) => void;
}) {
  const set = (patch: Partial<InviteConsents>) =>
    onChange({ ...consents, ...patch });
  const playerNoun = playerCount === 1 ? "your player" : "your players";

  return (
    <fieldset
      data-testid="consent-panel"
      data-invite-consent-panel="true"
      className="flex flex-col gap-2 rounded border border-rule bg-canvas p-3"
    >
      <legend className="px-1 font-display text-[0.65rem] uppercase tracking-wider text-ink-muted">
        Beta consent
      </legend>
      <ConsentCheckbox
        checked={consents.agreement}
        onChange={(v) => set({ agreement: v })}
        testId="invite-consent-agreement"
      >
        I agree to the{" "}
        <a
          href="/terms"
          target="_blank"
          rel="noreferrer"
          className="text-accent hover:underline"
        >
          Beta Participation Agreement
        </a>
        .
      </ConsentCheckbox>
      <ConsentCheckbox
        checked={consents.privacy_notice}
        onChange={(v) => set({ privacy_notice: v })}
        testId="invite-consent-privacy"
      >
        I have read the{" "}
        <a
          href="/privacy"
          target="_blank"
          rel="noreferrer"
          className="text-accent hover:underline"
        >
          Privacy Notice
        </a>
        .
      </ConsentCheckbox>

      {playerCount > 0 ? (
        <div className="mt-1 flex flex-col gap-2 border-t border-rule pt-2">
          <p className="text-[0.65rem] font-mono text-ink-muted">
            This links {playerCount === 1 ? "1 player" : `${playerCount} players`}{" "}
            to your account. As their parent or guardian:
          </p>
          <ConsentCheckbox
            checked={Boolean(consents.player_roster)}
            onChange={(v) => set({ player_roster: v })}
            testId="invite-consent-player-roster"
          >
            A roster entry (name, jersey number, team) may be kept for{" "}
            {playerNoun}.
          </ConsentCheckbox>
          <ConsentCheckbox
            checked={Boolean(consents.player_video)}
            onChange={(v) => set({ player_video: v })}
            testId="invite-consent-player-video"
          >
            Match and training video in which {playerNoun} appear may be
            uploaded and streamed, visible to our team only.
          </ConsentCheckbox>
          <ConsentCheckbox
            checked={Boolean(consents.player_feedback)}
            onChange={(v) => set({ player_feedback: v })}
            testId="invite-consent-player-feedback"
          >
            Coaches may create coaching feedback about {playerNoun},
            including private coaching working notes not visible to players
            or parents.
          </ConsentCheckbox>
          <ConsentCheckbox
            checked={Boolean(consents.player_photo)}
            onChange={(v) => set({ player_photo: v })}
            testId="invite-consent-player-photo"
          >
            <span className="text-ink-muted">(Optional)</span> A profile
            photo may be added to the roster entry.
          </ConsentCheckbox>
        </div>
      ) : null}
    </fieldset>
  );
}
